import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getAuthHeaders } from "../../utils/apiHeaders";

var API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export var fetchCart = createAsyncThunk(
  "cart/fetchCart",
  async function (language, thunkApi) {
    try {
      var response = await fetch(API_BASE_URL + "/cart", {
        method: "GET",
        signal: thunkApi.signal,
        headers: getAuthHeaders(language),
      });

      var responseData = await response.json();

      if (!response.ok) {
        return thunkApi.rejectWithValue({
          message: responseData.message || "Unable to retrieve cart",
        });
      }

      return responseData.data;
    } catch (error) {
      return thunkApi.rejectWithValue({
        message: error.message || "Unable to connect to the server",
      });
    }
  }
);

export var addToCart = createAsyncThunk(
  "cart/addToCart",
  async function (payload, thunkApi) {
    try {
      var language = payload.language;
      var body = payload.itemType
        ? { item_id: payload.itemId, item_type: payload.itemType }
        : { book_id: payload.bookId };

      var response = await fetch(API_BASE_URL + "/cart", {
        method: "POST",
        signal: thunkApi.signal,
        headers: Object.assign(
          { "Content-Type": "application/json" },
          getAuthHeaders(language)
        ),
        body: JSON.stringify(body),
      });

      var responseData = await response.json();

      if (!response.ok) {
        return thunkApi.rejectWithValue({
          message: responseData.message || "Unable to add item to cart",
          code: response.status,
        });
      }

      thunkApi.dispatch(fetchCart(language));
      return responseData.data;
    } catch (error) {
      return thunkApi.rejectWithValue({
        message: error.message || "Unable to connect to the server",
      });
    }
  }
);

export var removeCartItem = createAsyncThunk(
  "cart/removeCartItem",
  async function (payload, thunkApi) {
    try {
      var language = payload.language;
      var response = await fetch(
        API_BASE_URL +
          "/cart/items/" +
          encodeURIComponent(payload.itemType) +
          "/" +
          encodeURIComponent(payload.itemId),
        {
          method: "DELETE",
          signal: thunkApi.signal,
          headers: getAuthHeaders(language),
        }
      );

      var responseData = await response.json();

      if (!response.ok) {
        return thunkApi.rejectWithValue({
          message: responseData.message || "Unable to remove item",
        });
      }

      thunkApi.dispatch(fetchCart(language));
      return responseData.data;
    } catch (error) {
      return thunkApi.rejectWithValue({
        message: error.message || "Unable to connect to the server",
      });
    }
  }
);

export var clearCart = createAsyncThunk(
  "cart/clearCart",
  async function (language, thunkApi) {
    try {
      var response = await fetch(API_BASE_URL + "/cart", {
        method: "DELETE",
        signal: thunkApi.signal,
        headers: getAuthHeaders(language),
      });

      var responseData = await response.json();

      if (!response.ok) {
        return thunkApi.rejectWithValue({
          message: responseData.message || "Unable to clear cart",
        });
      }

      thunkApi.dispatch(fetchCart(language));
      return responseData.data;
    } catch (error) {
      return thunkApi.rejectWithValue({
        message: error.message || "Unable to connect to the server",
      });
    }
  }
);

var initialState = {
  cart: null,

  cartLoading: false,
  cartError: "",

  addLoading: false,
  addError: "",
  addErrorCode: null,

  removeLoading: false,
  removeError: "",

  clearLoading: false,
  clearError: "",
};

var cartSlice = createSlice({
  name: "cart",
  initialState: initialState,
  reducers: {
    clearAddError: function (state) {
      state.addError = "";
      state.addErrorCode = null;
    },
    resetCartState: function (state) {
      state.cart = null;
    },
  },
  extraReducers: function (builder) {
    builder
      .addCase(fetchCart.pending, function (state) {
        state.cartLoading = true;
        state.cartError = "";
      })
      .addCase(fetchCart.fulfilled, function (state, action) {
        state.cartLoading = false;
        state.cart = action.payload;
      })
      .addCase(fetchCart.rejected, function (state, action) {
        state.cartLoading = false;
        state.cartError =
          (action.payload && action.payload.message) || "Unable to retrieve cart";
      })

      .addCase(addToCart.pending, function (state) {
        state.addLoading = true;
        state.addError = "";
        state.addErrorCode = null;
      })
      .addCase(addToCart.fulfilled, function (state) {
        state.addLoading = false;
      })
      .addCase(addToCart.rejected, function (state, action) {
        state.addLoading = false;
        state.addError =
          (action.payload && action.payload.message) || "Unable to add item to cart";
        state.addErrorCode = action.payload && action.payload.code;
      })

      .addCase(removeCartItem.pending, function (state) {
        state.removeLoading = true;
        state.removeError = "";
      })
      .addCase(removeCartItem.fulfilled, function (state) {
        state.removeLoading = false;
      })
      .addCase(removeCartItem.rejected, function (state, action) {
        state.removeLoading = false;
        state.removeError =
          (action.payload && action.payload.message) || "Unable to remove item";
      })

      .addCase(clearCart.pending, function (state) {
        state.clearLoading = true;
        state.clearError = "";
      })
      .addCase(clearCart.fulfilled, function (state) {
        state.clearLoading = false;
      })
      .addCase(clearCart.rejected, function (state, action) {
        state.clearLoading = false;
        state.clearError =
          (action.payload && action.payload.message) || "Unable to clear cart";
      });
  },
});

export var clearAddError = cartSlice.actions.clearAddError;
export var resetCartState = cartSlice.actions.resetCartState;
export default cartSlice.reducer;
